import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useApiClient } from '../contexts/ApiContext';
import { useIsAuthed } from '../hooks/useIsAuthed';
import { queryKeys } from '../lib/queryClient';
import { useAuthedQuery } from './useAuthedQuery';
import { AuthBootstrapContext } from '../components/AuthBootstrap';
import { playEarnings } from '../lib/uiSounds';

export function useProfile(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.profile,
    queryFn: () => apiClient.api.userControllerGetProfile(),
    enabled,
  });
}

export function useProfileStats(userId?: string, enabled = true) {
  const apiClient = useApiClient();
  const isAuthed = useIsAuthed();
  const bootstrapCtx = useContext(AuthBootstrapContext);
  const bootstrapReady = !bootstrapCtx || bootstrapCtx.status === 'ready';

  // Public profile query: fires for anonymous visitors when `userId` is
  // provided (viewing someone else's profile). Can't use `useAuthedQuery`
  // here because that would gate strictly on auth — we want auth OR userId.
  // For the OWN-profile case (no userId arg), wait for AuthBootstrap to
  // finish: register + web-session must complete before the backend can
  // resolve `request.userId` from the session, otherwise the @Public
  // endpoint falls into the "userId query param required for anonymous
  // access" 401 even though the visitor is authenticated.
  return useQuery({
    queryKey: queryKeys.profileStats(userId),
    queryFn: () => apiClient.api.userControllerGetProfileStats({ userId }),
    enabled: enabled && (!!userId || (isAuthed && bootstrapReady)),
  });
}

export function useXpStatus(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.xpStatus,
    queryFn: () => apiClient.api.xpControllerGetXpStatus(),
    enabled,
  });
}

export function useXpConfig(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.xpConfig,
    queryFn: () => apiClient.api.xpControllerGetXpConfig(),
    enabled,
  });
}

export function useLevelBonuses(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.levelBonuses,
    queryFn: () => apiClient.api.xpControllerGetLevelBonusesStatus(),
    enabled,
  });
}

export function useClaimLevelBonus() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (level: number) => apiClient.api.xpControllerClaimLevelBonus(level),
    onSuccess: () => {
      playEarnings();
      queryClient.invalidateQueries({ queryKey: queryKeys.levelBonuses });
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useUpdateAnimationSetting() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (animationsEnabled: boolean) =>
      apiClient.api.profileSettingsControllerUpdateAnimationSetting({ animationsEnabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useToggleNotifications() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (enabled: boolean) =>
      apiClient.api.profileSettingsControllerToggleNotifications({ enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

// Helper hook for profile optimistic updates
export function useProfileUpdater() {
  const queryClient = useQueryClient();

  const updateBalance = (balanceInCredits: number) => {
    queryClient.setQueryData(queryKeys.profile, (old: any) =>
      old ? { ...old, balanceInCredits } : old
    );
  };

  const updateAnimationsEnabled = (animationsEnabled: boolean) => {
    queryClient.setQueryData(queryKeys.profile, (old: any) =>
      old ? { ...old, animationsEnabled } : old
    );
  };

  const updateSteamTradeUrl = (steamTradeUrl: string) => {
    queryClient.setQueryData(queryKeys.profile, (old: any) =>
      old
        ? { ...old, steamTradeUrl, lastTradeLinkChangedAt: new Date().toISOString() }
        : old
    );
  };

  const setProfile = (profile: any) => {
    queryClient.setQueryData(queryKeys.profile, profile);
  };

  return {
    updateBalance,
    updateAnimationsEnabled,
    updateSteamTradeUrl,
    setProfile,
  };
}

// Convenience selector hooks (replacing Zustand selectors)
export function useProfileData() {
  const { data: profile } = useProfile();
  return profile ?? null;
}

export function useBalance() {
  const { data: profile } = useProfile();
  return profile?.balanceInCredits ?? 0;
}
